import { useLanguage } from "../contexts/LanguageContext"
import { useRef } from "react"
import { useIntersectionObserver } from "../hooks/useScrollAnimation"
import {
  TruckIcon,
  MapPinIcon,
  ClockIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  DevicePhoneMobileIcon,
} from "@heroicons/react/24/outline"

export default function Features() {
  const { t } = useLanguage()
  const sectionRef = useRef(null)
  const isVisible = useIntersectionObserver(sectionRef)

  const features = [
    {
      key: "realTimeTracking",
      icon: MapPinIcon,
      color: "text-blue-500",
    },
    {
      key: "securePayments",
      icon: ShieldCheckIcon,
      color: "text-green-500",
    },
    {
      key: "instantBooking",
      icon: ClockIcon,
      color: "text-purple-500",
    },
    {
      key: "vehicleVariety",
      icon: TruckIcon,
      color: "text-[#FF8500]",
    },
    {
      key: "costEffective",
      icon: CurrencyDollarIcon,
      color: "text-yellow-500",
    },
    {
      key: "mobileApp",
      icon: DevicePhoneMobileIcon,
      color: "text-indigo-500",
    },
  ]

  return (
    <section id="features" className="py-20 bg-gradient-to-br from-gray-50 to-white" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`text-center mb-16 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-6">{t("features.title")}</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t("features.subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => {
            const IconComponent = feature.icon
            return (
              <div
                key={feature.key}
                className={`text-center p-6 rounded-xl bg-gradient-to-br from-orange-50/50 to-white shadow-lg border border-orange-100/60 hover:shadow-2xl hover:scale-105 hover:border-[#FF8500]/30 hover:bg-gradient-to-br hover:from-orange-100/40 hover:to-orange-50/60 transition-all duration-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                  }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-100/80 to-orange-50 rounded-full flex items-center justify-center hover:bg-gradient-to-br hover:from-[#FF8500]/20 hover:to-[#FF8500]/10 transition-all duration-300 group shadow-inner">
                    <IconComponent
                      className={`h-8 w-8 ${feature.color} group-hover:scale-110 transition-transform duration-300`}
                    />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 hover:text-[#FF8500] transition-colors duration-300">
                  {t(`features.${feature.key}.title`)}
                </h3>
                <p className="text-gray-600">{t(`features.${feature.key}.description`)}</p>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
